import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { object, string, number } from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IVehicleType } from "src/common/typings/vehicle";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    marginBottom: "20px",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IVehicleType {}

interface Props {
  // In TS we make an interface of Props to shape them.
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IVehicleType; // '?' Its means initialValues is Optional

  tableRecord?: any;
  temp?: any;
  loading?: any;
}

export function VehicleTypeInputs({
  initialValues,
  handleSubmit,
  updateSubmit,
  loading,

  tableRecord,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const gridLoading = false;
  const [isEditMode, setisEditMode] = React.useState(false);
  const [submitCounter, setSubmitCounter] = useState<any>(0);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
    weight: number()
      .required("This field is required")
      .min(1, "Minimum 1 is allowed.")
      .max(9999999999, "Maximum 10 digits are allowed."),
    fixedCharges: number()
      .required("This field is required")
      .min(1, "Minimum 1 is allowed.")
      .max(9999999999, "Maximum 10 digits are allowed."),
    demurrageCharges: number()
      .required("This field is required")
      .min(1, "Minimum 1 is allowed.")
      .max(9999999999, "Maximum 10 digits are allowed."),
  });

  // Form Start
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      id: initialValues?.id || 0,
      name: initialValues?.name || "",
      weight: initialValues?.weight || "",
      weightUnit: "KG",
      fixedCharges: initialValues?.fixedCharges || "",
      demurrageCharges: initialValues?.demurrageCharges || "",
      currencyUnit: "PKR",
      isActive: initialValues?.isActive || Boolean(false),
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      if (isEditMode === false && isDuplicatedRecordInTable(tableRecord, values.name) !== -1) {
        dispatch(
          addNotificationAction({
            message: "Record already exist.",
            options: { variant: "error" },
          })
        );
        return;
      }

      if (isEditMode) {
        await updateSubmit(values);
      } else {
        formik.setFieldValue("id", 0);
        await handleSubmit(values);
      }

      actions.setSubmitting(false);
      actions.resetForm();
      setisEditMode(false);
      setIsDirtyValue(false);
      setSelectedRecord(null);
    },
  });

  const handleUpdateClick = (params: any) => {
    setSelectedRecord(params);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("weight", params.weight);
    formik.setFieldValue("fixedCharges", params.fixedCharges);
    formik.setFieldValue("demurrageCharges", params.demurrageCharges);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("isActive", params.isActive);
    setisEditMode(true);
  };
  const isDuplicatedRecordInTable = (array: any, value: any) => {
    return array.findIndex((i: any) => i.name === value);
  };

  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);

  /* GRID Code */
  useEffect(() => {
    if (tableRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "weight"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return `${
                  row.original.weight
                    ? ` ${row.original.weight.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })} ${row.original.weightUnit ? row.original.weightUnit : "PKR"} `
                    : ""
                }`;
              },
            }
          : column.accessorKey === "fixedCharges"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return `${
                  row.original.fixedCharges
                    ? ` ${row.original.fixedCharges.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })} ${row.original.currencyUnit ? row.original.currencyUnit : "PKR"} `
                    : ""
                }`;
              },
            }
          : column.accessorKey === "demurrageCharges"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return `${
                  row.original.demurrageCharges
                    ? ` ${row.original.demurrageCharges.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })} ${row.original.currencyUnit ? row.original.currencyUnit : "PKR"} `
                    : ""
                }
                `;
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [tableRecord]);

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleValueChange(e: any) {
    // Textfield ==>> e.target.value
    // Checkbox / Switch ==>> e.target.checked !== e.target._wrapperState.initialChecked

    formik.handleChange(e);
    e.target.value ? setIsDirtyValue(true) : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} md={12} lg={12} spacing={3}>
                  <Grid item xs={12} md={4} lg={3} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Add type of Vehicle"
                        name="name"
                        placeholder="Add type of Vehicle"
                        disabled={false}
                        value={formik.values.name}
                        onChange={handleValueChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={2} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Weight (KG)"
                        name="weight"
                        placeholder="Enter Weight"
                        disabled={false}
                        value={formik.values.weight}
                        onChange={handleValueChange}
                        error={formik.touched.weight && Boolean(formik.errors.weight)}
                        helperText={formik.touched.weight && formik.errors.weight}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} lg={2} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Fixed Charges (PKR)"
                        name="fixedCharges"
                        placeholder="Enter Charges"
                        disabled={false}
                        value={formik.values.fixedCharges}
                        onChange={handleValueChange}
                        error={formik.touched.fixedCharges && Boolean(formik.errors.fixedCharges)}
                        helperText={formik.touched.fixedCharges && formik.errors.fixedCharges}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} lg={3} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        type="number"
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Demurrage Charges (PKR)"
                        name="demurrageCharges"
                        placeholder="Enter Demurrage Charges"
                        disabled={false}
                        value={formik.values.demurrageCharges}
                        onChange={handleValueChange}
                        error={
                          formik.touched.demurrageCharges && Boolean(formik.errors.demurrageCharges)
                        }
                        helperText={
                          formik.touched.demurrageCharges && formik.errors.demurrageCharges
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3} lg={1} style={{ paddingLeft: "0px" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3} lg={1}>
                    <Box mb={2} mt={1} ml={1} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? formik.values.name === selectedRecord.name &&
                              formik.values.weight === selectedRecord.weight &&
                              formik.values.fixedCharges === selectedRecord.fixedCharges &&
                              formik.values.demurrageCharges === selectedRecord.demurrageCharges &&
                              formik.values.isActive === selectedRecord.isActive
                            : JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => setSubmitCounter((prev: any) => prev + 1)}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>

                  <CommonMaterialGrid
                    defaultPerPageRecord={10}
                    height={400}
                    loader={gridLoading}
                    rows={tableRecord || []}
                    columns={displayColumns}
                  />
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
